import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSessions } from '@/lib/sessions';
import {
  Plus, X, Copy, Pin, Archive, MessageSquare, ListChecks, ChevronDown, RotateCcw, Trash2,
} from 'lucide-react';

/**
 * Browser-style tab bar for Diagnostic Chat / Guided Diagnosis sessions.
 *
 * Renders only the sessions of the current "kind" (chat or guided). Clicking a tab
 * activates that session; the page reads/writes state directly from the active session.
 * Adding a new tab creates a fresh empty session. The chevron dropdown exposes archived
 * sessions and per-tab actions (duplicate, pin, archive, delete).
 *
 * Props:
 *   kind: 'chat' | 'guided'   — which workspace this bar represents
 */
export default function DiagnosticTabBar({ kind }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    chatSessions, guidedSessions, activeIds,
    setActive, createSession, archiveSession, deleteSession,
    duplicateSession, togglePin, listArchived, unarchive,
  } = useSessions();
  const sessions = kind === 'chat' ? chatSessions : guidedSessions;
  const activeId = activeIds[kind] || null;
  const [menuFor, setMenuFor] = useState(null); // session id whose context-menu is open
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archived, setArchived] = useState([]);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef();

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const newTab = async () => {
    setBusy(true);
    try {
      await createSession(kind);
    } finally { setBusy(false); }
  };

  const openArchive = async () => {
    setArchiveOpen(true);
    try {
      const items = await listArchived(kind);
      setArchived(items);
    } catch { /* toast handled elsewhere */ }
  };

  const handleResume = async (sid) => {
    setBusy(true);
    try {
      const restored = await unarchive(sid);
      setActive(kind, restored.id);
      setArchiveOpen(false);
      // Make sure we land on the right page after restoring
      navigate(kind === 'chat' ? '/diagnosi' : '/diagnosi-guidata');
    } finally { setBusy(false); }
  };

  const handleClickTab = (sid) => {
    setActive(kind, sid);
    setMenuFor(null);
  };

  // Inline-rename: double-click on the active tab opens a tiny input.
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const startRename = (s) => {
    setRenamingId(s.id);
    setRenameValue(s.title || '');
  };
  const commitRename = async (s) => {
    const newTitle = (renameValue || '').trim();
    setRenamingId(null);
    if (!newTitle || newTitle === s.title) return;
    const { scheduleUpdate } = useSessionsRef.current;
    scheduleUpdate(s.id, { title: newTitle });
  };

  // We need a ref to scheduleUpdate without re-deriving the value
  const sessionsHook = useSessions();
  const useSessionsRef = useRef(sessionsHook);
  useEffect(() => { useSessionsRef.current = sessionsHook; });

  const Icon = kind === 'chat' ? MessageSquare : ListChecks;
  const kindLabelKey = kind === 'chat' ? 'chat.title' : 'guided.title';

  return (
    <div className="border-b border-gray-200 bg-white" data-testid={`diag-tabs-${kind}`}>
      <div className="flex items-stretch overflow-x-auto no-scrollbar">
        {sessions.length === 0 ? (
          <button
            onClick={newTab}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
            data-testid={`diag-tab-create-${kind}`}
          >
            <Plus className="w-3.5 h-3.5" />
            {t('sessions.new_session') || 'Nuova sessione'} {t(kindLabelKey)}
          </button>
        ) : (
          <>
            {sessions.map((s) => {
              const isActive = s.id === activeId;
              const isRenaming = renamingId === s.id;
              return (
                <div
                  key={s.id}
                  className={[
                    'group relative flex items-center gap-1.5 px-3 py-2 border-r border-gray-200 cursor-pointer min-w-[140px] max-w-[260px] transition-colors',
                    isActive ? 'bg-[#002FA7]/5 border-b-2 border-b-[#002FA7] -mb-px' : 'hover:bg-gray-50 border-b-2 border-b-transparent',
                  ].join(' ')}
                  onClick={() => !isRenaming && handleClickTab(s.id)}
                  onDoubleClick={() => isActive && startRename(s)}
                  data-testid={`diag-tab-${s.id}`}
                  data-active={isActive ? 'true' : 'false'}
                  title={`${s.title || s.brand + ' ' + s.model} — ${t('sessions.dblclick_rename') || 'doppio click per rinominare'}`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                  {s.pinned && <Pin className="w-3 h-3 shrink-0 text-amber-600" fill="currentColor" />}
                  {isRenaming ? (
                    <input
                      autoFocus
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => commitRename(s)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(s);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      className="text-xs font-medium bg-white border border-gray-300 px-1 py-0.5 max-w-[160px] outline-none focus:border-[#002FA7]"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span
                      className={`text-xs truncate ${isActive ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                    >
                      {s.title || s.brand || (t('sessions.untitled') || 'Senza titolo')}
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === s.id ? null : s.id); }}
                    className="p-0.5 text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                    data-testid={`diag-tab-menu-${s.id}`}
                    aria-label="menu"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); archiveSession(s.id); }}
                    className="p-0.5 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`diag-tab-close-${s.id}`}
                    aria-label="close"
                    title={t('sessions.close') || 'Chiudi sessione'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {menuFor === s.id && (
                    <div
                      ref={menuRef}
                      className="absolute top-full right-0 z-30 mt-0.5 bg-white border border-gray-200 shadow-lg min-w-[180px] py-1"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`diag-tab-menu-pop-${s.id}`}
                    >
                      <button onClick={() => { startRename(s); setMenuFor(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2">
                        <span className="font-mono text-gray-400">A|</span> {t('sessions.rename') || 'Rinomina'}
                      </button>
                      <button onClick={() => { togglePin(s.id); setMenuFor(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2">
                        <Pin className="w-3 h-3" /> {s.pinned ? (t('sessions.unpin') || 'Rimuovi pin') : (t('sessions.pin') || 'Fissa tab')}
                      </button>
                      <button onClick={() => { duplicateSession(s.id); setMenuFor(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2">
                        <Copy className="w-3 h-3" /> {t('sessions.duplicate') || 'Duplica'}
                      </button>
                      <button onClick={() => { archiveSession(s.id); setMenuFor(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 text-amber-700">
                        <Archive className="w-3 h-3" /> {t('sessions.archive') || 'Archivia'}
                      </button>
                      <button onClick={() => { if (window.confirm(t('sessions.delete_confirm') || 'Eliminare definitivamente questa sessione?')) { deleteSession(s.id); setMenuFor(null); } }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 flex items-center gap-2 text-red-700">
                        <Trash2 className="w-3 h-3" /> {t('app.delete') || 'Elimina'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <button
              onClick={newTab}
              disabled={busy}
              className="flex items-center gap-1 px-3 py-2 text-xs text-gray-500 hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors shrink-0"
              data-testid={`diag-tab-new-${kind}`}
              title={t('sessions.new_tab') || 'Nuova tab'}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        <div className="flex-1" />
        <button
          onClick={openArchive}
          className="flex items-center gap-1 px-3 py-2 text-[10px] font-mono uppercase text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors shrink-0 border-l border-gray-200"
          data-testid={`diag-tab-archive-${kind}`}
        >
          <Archive className="w-3 h-3" />
          {t('sessions.archive_list') || 'Sessioni archiviate'}
        </button>
      </div>

      {archiveOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 pt-24 animate-fade-up" onClick={() => setArchiveOpen(false)} data-testid="archive-modal">
          <div className="bg-white border border-gray-200 max-w-2xl w-full max-h-[70vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-3">
              <Archive className="w-4 h-4 text-gray-500" />
              <h3 className="font-display font-bold flex-1">{t('sessions.archive_title') || 'Sessioni archiviate'}</h3>
              <button onClick={() => setArchiveOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {archived.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">{t('sessions.archive_empty') || 'Nessuna sessione archiviata'}</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {archived.map((s) => (
                    <li key={s.id} className="p-3 flex items-center gap-3 hover:bg-gray-50">
                      <Icon className="w-4 h-4 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{s.title || s.brand}</div>
                        <div className="text-xs text-gray-500 font-mono">{new Date(s.updated_at).toLocaleString()}</div>
                      </div>
                      <button onClick={() => handleResume(s.id)} className="btn-secondary text-xs" data-testid={`archive-resume-${s.id}`}>
                        <RotateCcw className="w-3 h-3" /> {t('sessions.resume') || 'Riprendi'}
                      </button>
                      <button onClick={() => { if (window.confirm(t('sessions.delete_confirm') || 'Eliminare definitivamente?')) deleteSession(s.id).then(() => listArchived(kind).then(setArchived)); }} className="btn-ghost text-xs text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
